import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  Avatar,
  AvatarGroup,
  Stepper,
  StepLabel,
  IconButton,
  Button,
  Box,
  Typography,
  Card,
  CardContent,
  CardHeader,
  Grid,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { DataGridPro } from '@mui/x-data-grid-pro';
import { useTheme } from "@mui/material/styles";

import Icon from "../../../components/icon";
import CustomChip from "../../../components/mui/chip";
import Tooltip, { tooltipClasses } from "@mui/material/Tooltip";

import SearchRecordsDialog from "../component/SearchRecords";
import { GetServiceRequests } from "../logic/GetServiceRequest";
import { GetEntityLinks, DeleteLinkRecord } from "../logic/LinkRecordLogic";



const LinkedRecordsComponent = ({customerID, requestID, setLoading, module, rules, onLinkedEntityFound}) => {

  const theme = useTheme();

  const [openSearchRecordsDlg, setOpenSearchRecordsDlg] = useState(false);
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 5,
  });
  const [linkedEntities, setLinkedEntities] = useState([]); 

  useEffect(() => {  
    if(customerID){
      fetchLinks();  
    }  
  }, [customerID]);

  const fetchLinks = async () => {
    setLoading(true);
    try {
      let requests = await GetServiceRequests(customerID, 0);
      const linkedEntities = await GetEntityLinks(requestID);      
      
      if(requests && requests.length > 0 && linkedEntities && linkedEntities.length > 0){
        const mergedData = linkedEntities.map(linkedEntity => {
          // Determine the "other" entity id
          let otherRequestId;
          let linkedFromEntityId;
          
          if (Number(linkedEntity.fromEntityId) === Number(requestID)) {
            // Current request is the source, so the linked one is the target.
            otherRequestId = linkedEntity.toEntityId;
            linkedFromEntityId = linkedEntity.fromEntityId;
          } else if (Number(linkedEntity.toEntityId) === Number(requestID)) {
            // Current request is the target, so the linked one is the source.
            otherRequestId = linkedEntity.fromEntityId;
            linkedFromEntityId = linkedEntity.fromEntityId;
          } else {
            // The link is not related to the current request, so skip it.
            return null;
          }
        
          // Find the details for the other request from your requests array.
          const requestData = requests.find(
            r => Number(r.requestID) === Number(otherRequestId)
          );
          if (!requestData) return null;

          let recordID_New;

          if (requestData.entityType === "Request") {
            recordID_New = `REQ-${requestData.requestID}`;
          } else if (requestData.entityType === "Assessment") {
            recordID_New = `AST-${requestData.requestID}`;
          } else if (requestData.entityType === "Estimate") {
            recordID_New = `EST-${requestData.requestID}`;
          } else if (requestData.entityType === "WorkOrder") {
            recordID_New = `WO-${requestData.requestID}`;
          } else {
            // Fallback case
            recordID_New = `REC00${requestData.requestID}`;
          }
        
          return {
            id: linkedEntity.id,
            recordID: recordID_New,
            recordType: requestData.entityType,
            jobsiteName: requestData.jobsiteName,
            createdDate: requestData.requestedOn,
            status: requestData.status,
            assignedTo: requestData?.assignedToUsers || "",
            tags: requestData.tags,
            linkedEntityID: linkedEntity.toEntityId,
            fromEntityId: linkedEntity.fromEntityId, 
            toEntityId: linkedEntity.toEntityId, 
            requestID: requestData.requestID,
            isAutoLinked: linkedEntity.isAutoLinked,
          };
        }).filter(data => data !== null);        
        
        setLinkedEntities(mergedData);
        
        const relevantLink = linkedEntities.find(
          entity => Number(entity.toEntityId) === Number(requestID)
        );
        
        if (relevantLink && onLinkedEntityFound) {
          onLinkedEntityFound(relevantLink.fromEntityId);
        }
        
      } else if (requests && requests.length > 0 && linkedEntities && linkedEntities.length === 0) {
        setLinkedEntities([]);
        if (onLinkedEntityFound) {
          onLinkedEntityFound(null);
        }
      }

    } catch(error){
      console.log(error);
      if (onLinkedEntityFound) {
        onLinkedEntityFound(null);
      }
    } finally {
      setLoading(false);
    }
  }

    const handleUnLinkRecord = async (record) => {
    
    // console.log(record)
    const linkedEntities = await DeleteLinkRecord(record.id, record.linkedEntityID);
    // console.log(linkedEntities)
    if(linkedEntities.isError === false){
      fetchLinks();      
    } else {
      fetchLinks();  
    }

  };

  const handleOpenSearchRecordsDlg = () => setOpenSearchRecordsDlg(true);
  const handleCloseSearchRecordsDlg = () => setOpenSearchRecordsDlg(false);

  const getInitials = (name) => {
    if (!name) return "";
    const nameParts = name?.split(" ") || "";
    return nameParts
      .map((part) => part[0]?.toUpperCase())
      .join("")
      .slice(0, 2);
  };

  const columns = [
    {
      flex: 0.1,
      minWidth: 140,
      field: "recordID",
      headerName: "Record ID",
      renderCell: (params) => {
        const { row } = params;

        return (
          <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
            <Box sx={{ display: "flex", flexDirection: "column" }}>
              {module === "creation" ? (
                <Typography               
                noWrap
                variant="body2"
                sx={{ color: "text.primary", fontWeight: 600 }}
              >
                {row.recordID}
              </Typography>
              )
            :
             (            
             <Typography
              component={Link}
              to={
                row.recordType === "Request"
                  ? `/requests/detail/${row.requestID}`
                  : row.recordType === "Assessment"
                  ? `/assessments/detail/${row.requestID}`
                  : row.recordType === "WorkOrder"
                  ? `/work-orders/detail/${row.requestID}`
                  : row.recordType === "Estimate"
                  ? `/estimates/detail/${row.requestID}`
                  : "#"
              }
              noWrap
              variant="body2"
              sx={{ color: "text.primary", fontWeight: 600 }}
            >
              {row.recordID}
            </Typography>
            ) 
            }
            </Box>
          </Box>
        );
      },
    },
    {
      flex: 0.1,
      minWidth: 160,
      field: "recordType",
      headerName: "Record Type",
      renderCell: (params) => {
        const { row } = params;

        return (
          <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
            <Box sx={{ display: "flex", flexDirection: "column" }}>
              <Typography
                noWrap
                variant="body2"
                sx={{ color: "text.primary", fontWeight: 600 }}
              >
                {row.recordType === "WorkOrder" ?  "Work Order" : row.recordType}
              </Typography>
            </Box>
          </Box>
        );
      },
    },
    {
      flex: 0.1,
      minWidth: 170,
      field: "jobsiteName",
      headerName: "Jobsite Name",
      renderCell: (params) => {
        const { row } = params;

        return (
          <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
            <Box sx={{ display: "flex", flexDirection: "column" }}>
              <Typography
                noWrap
                variant="body2"
                sx={{ color: "text.primary", fontWeight: 600 }}
              >
                {row.jobsiteName}
              </Typography>
            </Box>
          </Box>
        );
      },
    },
    {
      flex: 0.1,
      type: "date",
      minWidth: 160,
      headerName: "Created Date",
      field: "createdDate",
      valueGetter: (params) => new Date(params.value),
      renderCell: (params) => (
        <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
          <Typography variant="body2" sx={{ color: "text.primary" }}>
            {params.row.createdDate}
          </Typography>
        </Box>
      ),
    },
    {
      flex: 0.1,
      minWidth: 110,
      field: "status",
      headerName: "Status",
      renderCell: (params) => (
        <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
            <Box sx={{ display: "flex", flexDirection: "column" }}>
              <Typography
                noWrap
                variant="body2"
                sx={{ color: "text.primary", fontWeight: 600 }}
              >
                {params.row.status}
              </Typography>
            </Box>
          </Box>
      ),
    },
    {
      flex: 0.1,
      field: "assignedTo",
      minWidth: 130,
      headerName: "Assigned To",
      renderCell: (params) =>  {
        const { row } = params;
        const initials = getInitials(row.assignedTo)
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
            <AvatarGroup 
              className='pull-up' 
              max={4} 
              sx={{
                "& .MuiAvatar-root": { width: 30, height: 30 },
                "& .MuiAvatarGroup-avatar": { width: 30, height: 30 },
              }}
            >
              <Tooltip title={row.assignedTo}>
                <Avatar alt='Olivia Sparks' sx={{ fontSize: '13px' }}>{initials}</Avatar>
              </Tooltip>
            </AvatarGroup>
          </Box>
        )
      },
    },
    {
      flex: 0.1,
      field: "tags",
      minWidth: 160,
      headerName: "Tags",
      renderCell: (params) => {
        const { row } = params;
        return (
          <Box
            sx={{
              display: "flex",
              gap: 1,
              flexWrap: "nowrap",
              alignItems: "center",
              height: "100%",
            }}
          >
            {row.tags && row.tags.length > 0 ? (
              row.tags.map((tag, index) => (
                <CustomChip
                  key={index}
                  rounded
                  label={tag}
                  size="small"
                  color="info"
                  skin="light"
                  sx={{ lineHeight: 1 }}
                />
              ))
            ) : (
              <Typography variant="body2" color="textSecondary">
                No tags
              </Typography>
            )}
          </Box>
        );
      },
    },
    {
      flex: 0.1,
      field: "actions",
      minWidth: 80,
      headerName: "Unlink",
      renderCell: (params) => {
        const { row } = params;
        const isDisabled = row.isAutoLinked === true;
        return (
          <Tooltip title="Unlink Record">
            <span>
              <IconButton
                disabled={ isDisabled}
                color="secondary"                
                sx={{
                  "&:hover": {
                    backgroundColor: "transparent",
                    color: theme.palette.secondary.dark,
                  },
                }}
                onClick={() => {
                  if (!isDisabled) {
                    handleUnLinkRecord(row);
                  }
                }}
              >
                <Icon icon="tabler:circle-minus" />
              </IconButton>
            </span>
          </Tooltip>
        );
      },
    },
  ];

  return (
    <>
      <Grid container spacing={6}>
      <Grid item xs={12}>
        <Card>
          <CardHeader
           sx={{
    '& .MuiCardHeader-action': {
      margin: { xs: '0 auto', sm: 'inherit' }, // Center on mobile, default on desktop
      display: 'flex',
      justifyContent: 'center', // Center button horizontally
      width: { xs: '100%', sm: 'auto' } // Full width on mobile
    }
  }}
            // title="Linked Records"
            action={
              <Button
                onClick={handleOpenSearchRecordsDlg}
                variant="contained"
                color="main"
                startIcon={<Icon icon="tabler:link" />}
                //disabled={!rules.canEdit}
              >
                Search Records to Link
              </Button>
            }
          />
          <CardContent>
            {/* {linkedEntities && linkedEntities.length > 0 && ( */}
              <DataGridPro
              autoHeight
              rows={linkedEntities}
              columns={columns}
              // pageSizeOptions={[7, 10, 25, 50]}
              // paginationModel={paginationModel}
              // onPaginationModelChange={setPaginationModel}
              disableRowSelectionOnClick
              slots={{
                footer: () => null
              }}
              sx={{
                "& .MuiDataGrid-columnHeaderTitle": {
                  textTransform: "none",
                },
              }}
            />

            {/* )} */}
            
          </CardContent>
        </Card>
      </Grid>
      </Grid>

            {/* Search Records to Link Dialog */}
      <SearchRecordsDialog
      open={openSearchRecordsDlg}
      setOpen={setOpenSearchRecordsDlg}
      handleClose={handleCloseSearchRecordsDlg}
      customerID={customerID || 0}
      requestID={requestID}
      fetchLinks={fetchLinks}
      linkedRequests={linkedEntities}
    />
  </>
  );
}

export default LinkedRecordsComponent;